/**
 * API Error Types and RFC 7807 Parsing
 * T011: Implement ApiError class and parseError() for RFC 7807
 */

// ============================================================================
// Types
// ============================================================================

/** RFC 7807 Problem Details */
export interface ProblemDetail {
  type: string
  title: string
  status: number
  detail?: string
  instance?: string
  correlationId?: string
  action?: {
    type: string
    email?: string
    [key: string]: unknown
  }
}

/** Client-side error type discriminant */
export type ErrorKind = 'api' | 'network' | 'timeout' | 'unknown'

/** Extended error for all client-side issues */
export interface ApiError extends ProblemDetail {
  errorKind: ErrorKind
  retryAfter?: number
  availableCredits?: number
  requiredCredits?: number
}

// ============================================================================
// Error Parsing
// ============================================================================

/**
 * Parse an HTTP response into an ApiError
 */
export async function parseError(response: Response): Promise<ApiError> {
  const contentType = response.headers.get('content-type') || ''
  const retryAfter = parseRetryAfter(response.headers.get('retry-after'))

  // Try to parse as RFC 7807 Problem Details
  if (
    contentType.includes('application/problem+json') ||
    contentType.includes('application/json')
  ) {
    try {
      const body = await response.json()
      return {
        type: body.type || `https://api.verts.dev/errors/${response.status}`,
        title: body.title || getStatusText(response.status),
        status: body.status || response.status,
        detail: body.detail,
        instance: body.instance,
        correlationId: body.correlation_id || body.correlationId,
        action: body.action,
        errorKind: 'api',
        retryAfter,
        availableCredits: body.availableCredits,
        requiredCredits: body.requiredCredits,
      }
    } catch {
      // Fall through to default handling
    }
  }

  // Default error for non-JSON responses
  return {
    type: `https://api.verts.dev/errors/${response.status}`,
    title: getStatusText(response.status),
    status: response.status,
    detail: await safeGetText(response),
    errorKind: 'api',
    retryAfter,
  }
}

/**
 * Create a network error (fetch failed)
 */
export function createNetworkError(message: string): ApiError {
  return {
    type: 'https://api.verts.dev/errors/network',
    title: 'Network Error',
    status: 0,
    detail: message,
    errorKind: 'network',
  }
}

/**
 * Create a timeout error
 */
export function createTimeoutError(timeoutMs: number): ApiError {
  return {
    type: 'https://api.verts.dev/errors/timeout',
    title: 'Request Timeout',
    status: 0,
    detail: `Request timed out after ${timeoutMs}ms`,
    errorKind: 'timeout',
  }
}

/**
 * Create an unknown error wrapper
 */
export function createUnknownError(error: unknown): ApiError {
  const message = error instanceof Error ? error.message : String(error)
  return {
    type: 'https://api.verts.dev/errors/unknown',
    title: 'Unknown Error',
    status: 0,
    detail: message,
    errorKind: 'unknown',
  }
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Check if a value is an ApiError
 */
export function isApiError(value: unknown): value is ApiError {
  if (!value || typeof value !== 'object') {
    return false
  }
  const obj = value as Record<string, unknown>
  return (
    typeof obj.type === 'string' &&
    typeof obj.title === 'string' &&
    typeof obj.status === 'number' &&
    typeof obj.errorKind === 'string'
  )
}

/**
 * Check if error should trigger retry
 */
export function isRetryableError(error: ApiError): boolean {
  // Retry on network errors
  if (error.errorKind === 'network') {
    return true
  }

  // Retry on 5xx server errors (except 501 Not Implemented)
  if (
    error.errorKind === 'api' &&
    error.status >= 500 &&
    error.status !== 501
  ) {
    return true
  }

  return false
}

/**
 * Check if error is due to authentication failure
 */
export function isAuthError(error: ApiError): boolean {
  return error.status === 401
}

/**
 * Check if error is due to rate limiting
 */
export function isRateLimitError(error: ApiError): boolean {
  return error.status === 429
}

/**
 * Check if error is due to insufficient credits
 */
export function isInsufficientCreditsError(error: ApiError): boolean {
  return error.status === 402
}

/**
 * Check if error is due to filename conflict (file already exists)
 */
export function isFilenameExistsError(error: ApiError): boolean {
  return error.status === 409 && error.type.includes('conflict')
}

/**
 * Check if error is due to invalid filename
 */
export function isInvalidFilenameError(error: ApiError): boolean {
  return error.status === 400 && (error.detail?.includes('filename') ?? false)
}

/**
 * Get a user-friendly error message from an error
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    // Use detail if available, otherwise title
    return error.detail ?? error.title
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'An unexpected error occurred'
}

/**
 * Get a user-friendly error message for file upload errors
 */
export function getUploadErrorMessage(
  error: unknown,
  filename: string
): string {
  if (!isApiError(error)) {
    return `${filename}: ${error instanceof Error ? error.message : 'Upload failed'}`
  }

  // Provide user-friendly messages for specific error types
  if (isFilenameExistsError(error)) {
    return `${filename}: A file with this name already exists. Please rename the file or delete the existing one.`
  }

  if (isInvalidFilenameError(error)) {
    return `${filename}: Invalid filename. Use only letters, numbers, spaces, dots, dashes, and underscores. Must start and end with a letter or number.`
  }

  if (error.status === 413) {
    return `${filename}: File is too large.`
  }

  if (error.status === 402) {
    return `${filename}: Insufficient storage credits.`
  }

  // Default to the API error detail
  return `${filename}: ${error.detail ?? error.title}`
}

// ============================================================================
// Filename Validation
// ============================================================================

/**
 * Regex for valid filenames (matches gateway validation)
 * Allows: alphanumeric, -, _, ., space
 * Must start and end with alphanumeric
 */
const FILENAME_REGEX =
  /^[a-zA-Z0-9][a-zA-Z0-9._\- ]{0,253}[a-zA-Z0-9]$|^[a-zA-Z0-9]$/

/**
 * Validate a filename before upload
 * Returns null if valid, or an error message if invalid
 */
export function validateFilename(filename: string): string | null {
  if (!filename || filename.trim().length === 0) {
    return 'Filename cannot be empty'
  }

  if (filename.length > 255) {
    return 'Filename cannot exceed 255 characters'
  }

  if (
    filename.includes('..') ||
    filename.includes('/') ||
    filename.includes('\\')
  ) {
    return 'Filename cannot contain path separators'
  }

  if (!FILENAME_REGEX.test(filename)) {
    // Provide specific feedback
    if (!/^[a-zA-Z0-9]/.test(filename)) {
      return 'Filename must start with a letter or number'
    }
    if (!/[a-zA-Z0-9]$/.test(filename)) {
      return 'Filename must end with a letter or number'
    }
    return 'Filename can only contain letters, numbers, spaces, dots, dashes, and underscores'
  }

  return null
}

/**
 * Check if a filename is valid
 */
export function isValidFilename(filename: string): boolean {
  return validateFilename(filename) === null
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Parse Retry-After header value
 */
function parseRetryAfter(value: string | null): number | undefined {
  if (!value) return undefined

  // Try parsing as seconds
  const seconds = parseInt(value, 10)
  if (!isNaN(seconds)) {
    return seconds
  }

  // Try parsing as HTTP date
  const date = Date.parse(value)
  if (!isNaN(date)) {
    return Math.max(0, Math.ceil((date - Date.now()) / 1000))
  }

  return undefined
}

/**
 * Get status text for HTTP status code
 */
function getStatusText(status: number): string {
  const statusTexts: Record<number, string> = {
    400: 'Bad Request',
    401: 'Unauthorized',
    402: 'Payment Required',
    403: 'Forbidden',
    404: 'Not Found',
    405: 'Method Not Allowed',
    409: 'Conflict',
    413: 'Payload Too Large',
    422: 'Unprocessable Entity',
    429: 'Too Many Requests',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    504: 'Gateway Timeout',
  }
  return statusTexts[status] || 'Error'
}

/**
 * Safely get response text (may fail for already-read body)
 */
async function safeGetText(response: Response): Promise<string | undefined> {
  try {
    return await response.text()
  } catch {
    return undefined
  }
}
